import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { FadeUp } from "../motion/Reveal";
import { ArrowUpRight, Smartphone, Globe, Bot, Workflow } from "lucide-react";

const SERVICES = [
    {
        icon: Smartphone,
        num: "01",
        slug: "apps",
        title: "Apps Mobile",
        body: "iOS + Android nativos ou React Native. Do wireframe ao lançamento nas lojas, com performance de flagship e analytics do dia 1.",
        tags: ["React Native", "iOS", "Android", "App Store"],
        img: "https://images.unsplash.com/photo-1551650975-87deedd944c3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwzfHxhcHAlMjBkZXZlbG9wbWVudCUyMGludGVyZmFjZXxlbnwwfHx8fDE3ODQ3MjkzNDV8MA&ixlib=rb-4.1.0&q=85",
        span: "md:col-span-8",
    },
    {
        icon: Globe,
        num: "02",
        slug: "sites",
        title: "Sites & Landing",
        body: "Sites institucionais e páginas de alta conversão. Design de ponta, SEO técnico, Core Web Vitals verdes.",
        tags: ["Next.js", "React", "CMS"],
        img: "https://images.unsplash.com/photo-1607799279861-4dd421887fb3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHw0fHxhcHAlMjBkZXZlbG9wbWVudCUyMGludGVyZmFjZXxlbnwwfHx8fDE3ODQ3MjkzNDV8MA&ixlib=rb-4.1.0&q=85",
        span: "md:col-span-4",
    },
    {
        icon: Bot,
        num: "03",
        slug: "ia",
        title: "IA & Chatbots",
        body: "Agentes conversacionais, copilots internos e integrações com GPT, Claude e Gemini. Casos reais, ROI mensurável.",
        tags: ["GPT-5", "Claude", "RAG"],
        img: "https://images.pexels.com/photos/28494632/pexels-photo-28494632.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        span: "md:col-span-5",
    },
    {
        icon: Workflow,
        num: "04",
        slug: "automacao",
        title: "Automação",
        body: "RPA, workflows e integrações entre sistemas. Elimine 40h/mês de trabalho manual do seu time.",
        tags: ["n8n", "Zapier", "APIs"],
        img: null,
        span: "md:col-span-7",
    },
];

function SpotlightCard({ service, className = "" }) {
    const ref = useRef(null);
    function onMove(e) {
        const el = ref.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        el.style.setProperty("--mx", `${e.clientX - rect.left}px`);
        el.style.setProperty("--my", `${e.clientY - rect.top}px`);
    }
    const Icon = service.icon;
    return (
        <div
            ref={ref}
            onMouseMove={onMove}
            className={`spotlight-card group border border-white/10 bg-[#0a0a0a] p-8 md:p-10 flex flex-col justify-between clip-frame-sm min-h-[380px] md:min-h-[440px] relative overflow-hidden ${className}`}
            data-testid={`service-card-${service.num}`}
        >
            <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                    <span className="font-mono-tech text-[#00FF66] text-xs tracking-widest">
                        {service.num}
                    </span>
                    <span className="w-8 h-[1px] bg-[#00FF66]/50" />
                </div>
                <Icon
                    className="w-6 h-6 text-white/60 group-hover:text-[#00FF66] transition-colors"
                    strokeWidth={1.5}
                />
            </div>

            {service.img && (
                <div className="absolute right-0 top-0 w-[45%] h-full opacity-40 group-hover:opacity-70 transition-opacity duration-700 pointer-events-none">
                    <div className="w-full h-full clip-frame-sm overflow-hidden">
                        <img
                            src={service.img}
                            alt=""
                            className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-1000 scale-105 group-hover:scale-110"
                        />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/70 to-transparent" />
                </div>
            )}

            <div className="relative z-10 max-w-md">
                <h3 className="font-display uppercase text-3xl md:text-4xl text-white mb-4 tracking-tight">
                    {service.title}
                </h3>
                <p className="text-white/60 leading-relaxed mb-6">
                    {service.body}
                </p>
                <div className="flex flex-wrap gap-2 mb-6">
                    {service.tags.map((t) => (
                        <span
                            key={t}
                            className="text-xs font-mono-tech text-white/50 border border-white/15 px-3 py-1 rounded-full"
                        >
                            {t}
                        </span>
                    ))}
                </div>
                <a
                    href="#contato"
                    className="inline-flex items-center gap-2 text-sm text-white hover:text-[#00FF66] transition-colors group/link"
                    data-testid={`service-cta-${service.num}`}
                >
                    Contratar este serviço
                    <ArrowUpRight className="w-4 h-4 group-hover/link:translate-x-1 group-hover/link:-translate-y-1 transition-transform" />
                </a>
                {service.slug && (
                    <Link
                        to={`/servicos/${service.slug}`}
                        className="mt-3 inline-flex items-center gap-2 text-xs font-mono-tech uppercase tracking-widest text-white/50 hover:text-[#00FF66] transition-colors"
                        data-testid={`service-more-${service.num}`}
                    >
                        Saber mais →
                    </Link>
                )}
            </div>
        </div>
    );
}

export default function Services() {
    return (
        <section
            id="servicos"
            className="relative py-32 md:py-40 border-b border-white/10"
            data-testid="services-section"
        >
            <div className="max-w-[1600px] mx-auto px-6 md:px-10">
                <div className="grid grid-cols-12 gap-6 mb-20 items-end">
                    <FadeUp className="col-span-12 md:col-span-7">
                        <span className="font-mono-tech text-xs tracking-widest text-[#00FF66] uppercase">
                            (Serviços) · Tudo em um só lugar
                        </span>
                        <h2 className="mt-6 font-display uppercase text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tighter text-white">
                            Quatro
                            <br />
                            <span className="text-white/40">disciplinas.</span>
                            <br />
                            Um único time.
                        </h2>
                    </FadeUp>
                    <FadeUp
                        className="col-span-12 md:col-span-4 md:col-start-9"
                        delay={0.1}
                    >
                        <p className="text-white/60 text-lg leading-relaxed">
                            Da ideia rabiscada no guardanapo ao produto rodando
                            em produção — cobrimos toda a jornada digital da
                            sua empresa. Sem intermediários. Sem fricção.
                        </p>
                    </FadeUp>
                </div>

                <div className="grid grid-cols-12 gap-6">
                    {SERVICES.map((s, i) => (
                        <FadeUp
                            key={s.num}
                            delay={i * 0.06}
                            className={`col-span-12 ${s.span}`}
                        >
                            <SpotlightCard service={s} className="h-full" />
                        </FadeUp>
                    ))}
                </div>
            </div>
        </section>
    );
}
